import * as React from 'react';
import Media from 'react-media';
import HtmlContent from '../_common/html-content';
import Note from '../_common/note';
import LinkListGroup from '../_common/link-list-group';
import Image from '../_common/image';
import ReleaseNote from '../release-note';
import DataFlag from '../data-flag';
import RequestTrialButton from '../request-a-trial-button';
import Bookmark from '../_common/bookmark';
import ChatiqSearch from '../chatiq-search';
import buildImageUrl from '../../utilities/build-image-url';

import { getText, get, cn, superScriptSanitizer, getFlagType } from '../../helpers';

import './dataset-introduction-section.style.scss';

const renderLogo = (dataProfile: DataProfile) => {
    // dataset logos
    if (dataProfile.marketDatasetLogos) {
        return dataProfile.marketDatasetLogos
            .sort((a, b) => a.FormOrder - b.FormOrder)
            .map((Logo: MarketDatasetLogos) => (
                <Image key={Logo.OnlineImageFilePath} src={Logo.OnlineImageFilePath} alt={dataProfile.dataProvider || ''} />
            ));
    }
    return null;
};

const chatiqSearchDatasetText = getText('pages.llm.dataset.searchQText');
const chatiqSearchBundleText = getText('pages.llm.bundle.searchQText');
const chatiqDatasetPlaceHolder = getText('pages.llm.dataset.placeholder');
const chatiqBundlePlaceHolder = getText('pages.llm.bundle.placeholder');
const metaDataText = getText('pages.dataset.metaData');

const datasetIntroductionSection = function DatasetIntroductionSection({
    dataProfile,
    supportLinks,
    researchLinks,
    isLoggedIn,
    ratButtonElement,
    sticky,
    showRatButton,
    internalResourceLinks,
    isBundle,
    isInternal,
    datasetId
}: DatasetIntroductionSectionProps) {
    const bookmarkTooltip = getText(dataProfile.isBundle ? 'bookmark.tooltip.bundle' : 'bookmark.tooltip.dataset') as BookmarkTooltip[];
    const [isIpad, setIsIpad] = React.useState<boolean>(false);

    const checkIfTablet = () => {
        if (navigator.platform === 'iPhone' || navigator.platform === 'iPad'
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0)
            || navigator.userAgent.includes('Android') || (navigator.platform === 'Linux armv81' && 'ontouchstart' in window && window.innerWidth > window.innerHeight && window.innerHeight < 768)) {
            setIsIpad(true);
        } else setIsIpad(false);
    };

    const handleResize = () => {
        checkIfTablet();
    };

    const url = dataProfile.isBundle ? `/datasets/bundle/${datasetId}` : `/datasets/${datasetId}`;
    const metaData = {
        title: `${dataProfile.name} ${metaDataText.subTitle}`,
        description: dataProfile.shortDescription,
        type: 'Dataset',
        url: `${url}`,
        image: buildImageUrl(dataProfile.socialSharingImageFilePath)
    };

    React.useEffect(() => {
        checkIfTablet();
    }, []);

    React.useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });

    return (
        <section className="dataset-introduction-section page-section border-bottom border-top margin-bottom-0">
            <div className="page-section__inner">
                <div className="two-column-layout">
                    <div className="dataset-introduction-section__overview two-column-layout__col">
                        <div className={getFlagType(dataProfile.addedDate, dataProfile.enhancementDate).startsWith('default') ? 'dataset-introduction-section__overview-row__same-row' : 'dataset-introduction-section__overview-row'}>
                            <div
                                className={cn('dataset-introduction-section__overview-logo', {
                                    'multiple-logo': dataProfile.marketDatasetLogos && dataProfile.marketDatasetLogos.length > 2
                                })}
                            >
                                {renderLogo(dataProfile)}
                            </div>
                            <div className="dataset-introduction-section__overview-flag">
                                <div className="dataset-introduction-section__overview-flag__group">
                                    <DataFlag
                                        datasetId={dataProfile.id}
                                        datasetName={dataProfile.name}
                                        datasetAddedDate={dataProfile.addedDate}
                                        datasetEnhancementDate={dataProfile.enhancementDate}
                                        variant="profile"
                                    />
                                    <Bookmark
                                        isLoggedIn={isLoggedIn}
                                        bookmarkTooltip={bookmarkTooltip}
                                        keyId={dataProfile.id}
                                        bookmarkType="datasets"
                                        longBookmarkIcon
                                    />
                                </div>
                            </div>
                        </div>
                        <h1>{superScriptSanitizer(dataProfile.name)}</h1>
                        {(dataProfile.hasAlert && dataProfile.alertNote) && (
                            <Note
                                title={dataProfile.alertTitle}
                                noteParagraph={dataProfile.alertNote}
                            />
                        )}
                        {(!dataProfile.hasAlert && dataProfile.releaseNote && dataProfile.enhancementDate) && (
                            <ReleaseNote
                                title={getText('generic.releaseNotes')}
                                noteParagraph={dataProfile.releaseNote}
                                linkText={getText('generic.readMore')}
                                linkHref={dataProfile.releaseNoteLink}
                                dateAdded={dataProfile.addedDate}
                                dateUpdated={dataProfile.enhancementDate}
                            />
                        )}
                        {!isIpad && (
                            <div className="chatiq-search-visibility">
                                <ChatiqSearch
                                    text={isBundle ? chatiqSearchBundleText : chatiqSearchDatasetText}
                                    placeholder={isBundle ? chatiqBundlePlaceHolder : chatiqDatasetPlaceHolder}
                                    variant={isBundle ? 'bundle' : 'dataset'}
                                    id={dataProfile.id}
                                    isInternal={isInternal || false}
                                    dataProfile={dataProfile}
                                    isLoggedIn={isLoggedIn}
                                />
                            </div>
                        )}
                        {dataProfile.longDescription && <HtmlContent className="dataset-introduction-section__overview-description" html={dataProfile.longDescription} />}
                        {dataProfile.dataProviderDesc && (
                            <div className="dataset-introduction-section__overview-vendor-info">
                                <h2 className="subheading subheading--with-border">{getText('generic.vendorInfo')}</h2>
                                <HtmlContent className="dataset-introduction-section__overview-vendor-info-body" html={dataProfile.dataProviderDesc} />
                            </div>
                        )}
                    </div>
                    <aside className="dataset-introduction-section__form two-column-layout__col">
                        <div
                            ref={ratButtonElement}
                            className={cn('dataset-introduction-section__rat-button-wrapper', {
                                'dataset-introduction-section__rat-button-wrapper--sticky': sticky,
                                'dataset-introduction-section__rat-button-wrapper--hidden': !showRatButton
                            })}
                        >
                            <div className="dataset-introduction-section__rat-bookmark-section bookmark-section">
                                <RequestTrialButton
                                    buttonText={dataProfile.marketplaceTrialButtonTitle}
                                    productName={dataProfile.name}
                                    productId={dataProfile.id.toString()}
                                    dataProvider={dataProfile.dataProvider}
                                    productVariant="dataset"
                                    className="dataset-introduction-section__rat-button"
                                    isLoggedIn={isLoggedIn}
                                    dataFilePath={dataProfile.marketMSADataFilePath}
                                    marketMSADataLink={dataProfile.marketMSADataLink}
                                    marketplaceMSATooltip={dataProfile.marketplaceMSATooltip}
                                    metaData={metaData}
                                />
                                {/* <Bookmark
                                    isLoggedIn={isLoggedIn}
                                    bookmarkTooltip={bookmarkTooltip}
                                    keyId={dataProfile.id}
                                    bookmarkType="datasets"
                                /> */}
                            </div>
                        </div>
                        <Media query={{ maxWidth: 1023 }}>
                            <div className="dataset-introduction-section__support-links">
                                {supportLinks && supportLinks.length > 0 && (
                                    <LinkListGroup list={supportLinks} title={getText('generic.supportTitle')} isAccordion />
                                )}
                                {researchLinks && researchLinks.length > 0 && (
                                    <LinkListGroup list={researchLinks} title={getText('generic.researchTitle')} isAccordion />
                                )}
                                {isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                    <LinkListGroup list={internalResourceLinks} title={getText('generic.internalResources')} isAccordion />
                                )}
                            </div>
                        </Media>
                        <Media query={{ minWidth: 1024 }}>
                            <div className="dataset-introduction-section__support-links">
                                {supportLinks && supportLinks.length > 0 && (
                                    <LinkListGroup list={supportLinks} title={getText('generic.supportTitle')} />
                                )}
                                {researchLinks && researchLinks.length > 0 && (
                                    <LinkListGroup list={researchLinks} title={getText('generic.researchTitle')} />
                                )}
                                {isLoggedIn && internalResourceLinks && internalResourceLinks.length > 0 && (
                                    <LinkListGroup list={internalResourceLinks} title={getText('generic.internalResources')} />
                                )}
                            </div>
                        </Media>
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default React.memo(datasetIntroductionSection);
